/**
 * Which viewer a record's source file gets in the detail modal's Source tab.
 *
 * The format is a property of the FILE, never of the record: a value lifted from a
 * table inside a deck is captured as a spreadsheet-shaped record (an in-memory
 * worksheet named "Slide 3 table 1"), but the document the reviewer must check is
 * still the .pptx. Dispatching on the file keeps those records out of the workbook
 * preview, which would have nothing to open.
 */

/** The source viewers available in the Source tab. */
export type SourceFormat = 'SPREADSHEET' | 'PDF' | 'PPTX' | 'IMAGE' | 'UNSUPPORTED';

/** MIME types the workbook preview can parse client-side. */
const SPREADSHEET_MIME_TYPES: ReadonlyArray<string> = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'text/csv'
];

const PPTX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

const EXTENSION_FORMATS: Readonly<Record<string, SourceFormat>> = {
  xlsx: 'SPREADSHEET',
  xlsm: 'SPREADSHEET',
  csv: 'SPREADSHEET',
  pdf: 'PDF',
  pptx: 'PPTX',
  png: 'IMAGE',
  jpg: 'IMAGE',
  jpeg: 'IMAGE'
};

/** The lower-cased extension of a filename (zip entries keep their inner path), or "". */
function fileExtension(fileName: string): string {
  const match = /\.([A-Za-z0-9]+)$/.exec(fileName.trim());
  return match === null ? '' : match[1].toLowerCase();
}

/**
 * Resolves which viewer to render for a source file. Prefers the server-reported
 * MIME type and falls back to the filename extension, so records captured before
 * the backend joined the MIME type still land on the right viewer.
 * @param {string} fileName The source file's original filename.
 * @param {string | null | undefined} mimeType The server-reported MIME type, when known.
 * @returns {SourceFormat} The viewer to render.
 */
export function resolveSourceFormat(fileName: string, mimeType?: string | null): SourceFormat {
  const declared = typeof mimeType === 'string' ? mimeType.split(';')[0].trim().toLowerCase() : '';
  if (SPREADSHEET_MIME_TYPES.includes(declared)) {
    return 'SPREADSHEET';
  }
  if (declared === 'application/pdf') {
    return 'PDF';
  }
  if (declared === PPTX_MIME_TYPE) {
    return 'PPTX';
  }
  if (declared.startsWith('image/')) {
    return 'IMAGE';
  }
  // Unknown or generic MIME (zip entries get a best-effort type server-side, and
  // some browsers send application/octet-stream): fall back to the extension.
  return EXTENSION_FORMATS[fileExtension(fileName)] ?? 'UNSUPPORTED';
}

/**
 * The Source tab's label. Spreadsheets keep the familiar "Worksheet"; every other
 * format is a document rather than a grid.
 * @param {SourceFormat} format The resolved source format.
 * @returns {string} The tab label.
 */
export function sourceTabLabel(format: SourceFormat): string {
  return format === 'SPREADSHEET' ? 'Worksheet' : 'Source document';
}
